import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  DenseTable,
  StatusBadge,
  Waterfall,
  type Column,
} from "../../components/index.ts";
import type { Span, TraceDetail } from "./use-traces.ts";
import {
  spanEventsHref,
  spanKindLabel,
  spanStatusLabel,
  spanStatusLevel,
  spanStatusTitle,
  toWaterfallSpans,
} from "./vocab.ts";

/**
 * The waterfall for ONE trace (task 11.11; design 12.2 "waterfall per trace across services"; 12.3
 * drill-down; 12.4 a11y). It draws the trace's spans with the shared `Waterfall` component (task
 * 11.2) and, below it, a semantic "spans and events" table that provides the per-span drill the
 * design asks for (a span → its events).
 *
 * THE INDEPENDENT inferred/partial RULE at the TRACE level is honoured by the `Waterfall` itself:
 * this component feeds the trace's two independent booleans straight into the Waterfall's
 * `inferred`/`partial` props, which the Waterfall renders as two separate trace badges — both when
 * both apply, neither taking precedence. There is NO per-span `inferred`/`partial` in the contract
 * ({@link SPAN_LEVEL_LABELS_AVAILABLE} is false), so no span-level badge is fabricated; where an
 * inferred trace was correlated, the honest `correlation` object is shown instead of a per-span
 * guess.
 *
 * The Waterfall is already the accessible, semantic span list (the data-table alternative), with
 * each span's name, service, status (icon+text) and timing as text. The companion table adds, per
 * span, the span kind and a keyboard-operable drill to the span's events — the one affordance the
 * presentational Waterfall does not itself carry.
 */
export function TraceWaterfall(props: { detail: TraceDetail }): ReactNode {
  const { detail } = props;
  const spans = toWaterfallSpans(detail);

  return (
    <section className="trc-detail" aria-label="Trace waterfall">
      {/* Honest correlation note for an inferred trace (how it was correlated, or nothing). */}
      <CorrelationNote detail={detail} />

      {/*
        Trace-level inferred/partial flow straight into the Waterfall's INDEPENDENT props. The
        Waterfall renders both badges when both are true, neither taking precedence (task 11.2).
      */}
      <Waterfall
        title={`${detail.root_span_name} · ${shortId(detail.trace_id)}`}
        spans={spans}
        totalMs={detail.duration_ms}
        inferred={detail.inferred}
        partial={detail.partial}
        emptyMessage="This trace recorded no spans in range."
      />

      {/* The per-span drill the design asks for (a span → its events). */}
      <SpanEventsTable detail={detail} />
    </section>
  );
}

/**
 * The honest `correlation` note. An `inferred` trace carries a `correlation` object (how it was
 * correlated — a decision id and/or agent, each possibly null); a non-inferred trace has `null`
 * correlation and this renders nothing. This surfaces the real reason a trace is inferred rather
 * than leaving the badge unexplained (design 12.3).
 */
function CorrelationNote(props: { detail: TraceDetail }): ReactNode {
  const { correlation, inferred } = props.detail;
  if (correlation === null) {
    // A non-inferred trace needs no note; an inferred trace with no correlation detail says so.
    if (!inferred) return null;
    return (
      <p className="trc-correlation" role="note">
        This trace is <strong>inferred</strong>: it was correlated without a propagated trace
        header. No further correlation detail was recorded.
      </p>
    );
  }
  const parts: string[] = [];
  if (correlation.decision_id !== null && correlation.decision_id !== undefined) {
    parts.push(`decision ${correlation.decision_id}`);
  }
  if (correlation.agent !== null && correlation.agent !== undefined) {
    parts.push(`agent ${correlation.agent}`);
  }
  return (
    <p className="trc-correlation" role="note">
      This trace is <strong>inferred</strong>: correlated by{" "}
      {parts.length > 0 ? parts.join(" and ") : "decision id, agent and time"} because no trace
      header could propagate.
    </p>
  );
}

/**
 * A semantic table of the trace's spans with a drill, per span, to that span's events. Spans are
 * shown in display order (same ordering the Waterfall uses). The service cell is the drill: it
 * navigates to the standing `/events?trace=…&component=…` filter for that span (design 12.3). The
 * span status is icon+text (never colour alone); the span kind is spelled out.
 */
function SpanEventsTable(props: { detail: TraceDetail }): ReactNode {
  const navigate = useNavigate();
  const { detail } = props;

  // Order the raw spans the same way the Waterfall orders them (by start, then name) for a match.
  const ordered = [...detail.spans].sort((a, b) => {
    const sa = a.start_ts;
    const sb = b.start_ts;
    if (sa !== sb) return sa - sb;
    return a.name.localeCompare(b.name);
  });

  const columns: readonly Column<Span>[] = [
    {
      key: "name",
      header: "Span",
      isRowHeader: true,
      cell: (s) => <span className="trc-span-name">{s.name}</span>,
    },
    {
      key: "service",
      header: "Service",
      cell: (s) => (
        <button
          type="button"
          className="trc-link"
          onClick={() => navigate(spanEventsHref(detail.trace_id, s))}
          aria-label={`Open the events for the ${s.service} span ${s.name}`}
        >
          <code>{s.service}</code>
        </button>
      ),
    },
    {
      key: "kind",
      header: "Kind",
      cell: (s) => <span className="trc-faint">{spanKindLabel(s.kind)}</span>,
    },
    {
      key: "status",
      header: "Status",
      cell: (s) => (
        <StatusBadge
          level={spanStatusLevel(s.status)}
          label={spanStatusLabel(s.status)}
          size="sm"
          title={spanStatusTitle(s.status)}
        />
      ),
    },
    {
      key: "duration",
      header: "Duration",
      align: "right",
      cell: (s) => (
        <span className="trc-num" title={`${s.duration_ms} ms`}>
          {formatMs(s.duration_ms)}
        </span>
      ),
    },
  ];

  return (
    <DenseTable
      caption="Spans in this trace. Open a span's service to see that span's events."
      columns={columns}
      rows={ordered}
      rowKey={(s) => s.span_id}
      emptyMessage="This trace recorded no spans in range."
    />
  );
}

/** Format a millisecond duration for display (`123 ms` / `1.23 s`), matching the Waterfall default. */
function formatMs(ms: number): string {
  if (!Number.isFinite(ms)) return "n/a";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

/** A short, readable form of a long trace id for the waterfall title. */
function shortId(id: string): string {
  return id.length <= 12 ? id : `${id.slice(0, 8)}…`;
}
